import React from "react";
import { useSearchParams } from "react-router-dom";

import {
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardHeader,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
  Table
} from "reactstrap";
import { ErrorHandler } from "../../common/utils/Error";

// Import the same logos used in MergeReceipt
import Logo from "@src/assets/images/logo/logo-icon.png";
import SakeenaLogo from "../../assets/images/logo/inv-lh-logo.png";
import TextHeader from "../../assets/images/logo/inv-lh-tx.png";
import AddowLogo from "../../assets/images/logo/inv-logo2.png";

import axios from "axios";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { FilePdf, Printer } from "react-bootstrap-icons";
import { toast } from "react-hot-toast";
import generatePDF, { Margin, Resolution } from "react-to-pdf";
import { useReactToPrint } from "react-to-print";
import { formatCurrency } from "../../utility/Utils";

export default function FinancialSummaryReport() {
  const [searchParams, setSearchParams] = useSearchParams();
  const appView = searchParams.get("view");

  const [apiData, setApiData] = useState({
    income: {},
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Date filter states
  const [filterType, setFilterType] = useState(
    searchParams.get("filterType") || "thisMonth"
  );
  const [showCustomRange, setShowCustomRange] = useState(
    searchParams.get("filterType") === "custom"
  );
  const [dateRange, setDateRange] = useState({
    from:
      searchParams.get("from") ||
      moment().startOf("month").format("YYYY-MM-DD"),
    to: searchParams.get("to") || moment().endOf("month").format("YYYY-MM-DD"),
  });

  const printRef = useRef();
  const downloadRef = useRef();

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `Financial Summary Report - ${dateRangeDisplay}`,
    pageStyle: `
    @page {
      size: A4 portrait;
      margin: 10mm !important;
    }
    @media print {
      @page {
        size: a4 portrait;
        margin: 10mm !important;
      }
    }
    
    @media print {
      html, body {
        height: initial !important;
        overflow: initial !important;
        -webkit-print-color-adjust: exact;
      }
    }
    
    @media print {
      .page-break {
        margin-top: 1rem;
        display: block;
        page-break-before: always !important;
      }
      .card{
        box-shadow: none !important;
        border: none !important;
      }
      .no-print {
        display: none !important;
      }
      .print-only {
        display: block !important;
      }
      .web-only {
        display: none !important;
      }
      .report-table th, .report-table td {
        padding: 0.75rem;
        border-color: #e8e8e8 !important;
      }
      .report-table th {
        background-color: #f8f8f8 !important;
        font-weight: 600;
      }
      .report-header {
        margin-bottom: 1.5rem;
      }
    }
    
     
    `,
    bodyClass: "",
  });

  const handleToPDF = () => {
    if (downloadRef.current) {
      downloadRef.current.style.display = "block";

      // Use generatePDF to create the PDF
      const options = {
        filename: "financial-summary.pdf",
        resolution: Resolution.HIGH,
        page: {
          margin: Margin.SMALL,
        },
        canvas: {
          qualityRatio: 1,
        },
        overrides: {
          pdf: {
            compress: true,
            format: "a4",
            unit: "mm",
          },
          canvas: {
            useCORS: true,
          },
        },
      };

      generatePDF(downloadRef, options)
        .then(() => {
          // Hide the element again after the PDF is generated
          downloadRef.current.style.display = "none";
        })
        .catch((error) => {
          console.error("Failed to generate PDF:", error);
          downloadRef.current.style.display = "none"; // Ensure it's hidden in case of an error
        });
    }
  };

  // Calculate date range based on filter type
  const getDateRangeByFilterType = (type) => {
    switch (type) {
      case "thisYear":
        return {
          from: moment().startOf("year").format("YYYY-MM-DD"),
          to: moment().endOf("year").format("YYYY-MM-DD"),
        };
      case "lastYear":
        return {
          from: moment()
            .subtract(1, "year")
            .startOf("year")
            .format("YYYY-MM-DD"),
          to: moment().subtract(1, "year").endOf("year").format("YYYY-MM-DD"),
        };
      case "thisMonth":
        return {
          from: moment().startOf("month").format("YYYY-MM-DD"),
          to: moment().endOf("month").format("YYYY-MM-DD"),
        };
      case "lastMonth":
        return {
          from: moment()
            .subtract(1, "month")
            .startOf("month")
            .format("YYYY-MM-DD"),
          to: moment().subtract(1, "month").endOf("month").format("YYYY-MM-DD"),
        };
      case "thisWeek":
        return {
          from: moment().startOf("week").format("YYYY-MM-DD"),
          to: moment().endOf("week").format("YYYY-MM-DD"),
        };
      default:
        return dateRange;
    }
  };

  // Handle filter type change
  const handleFilterTypeChange = (type) => {
    setFilterType(type);
    if (type === "custom") {
      setShowCustomRange(true);
    } else {
      setShowCustomRange(false);
      const newRange = getDateRangeByFilterType(type);
      setDateRange(newRange);

      // Update URL and fetch data
      updateSearchParamsAndFetch(type, newRange);
    }
  };

  const updateSearchParamsAndFetch = (type, range) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set("filterType", type);
    newSearchParams.set("from", range.from);
    newSearchParams.set("to", range.to);
    setSearchParams(newSearchParams);
  };

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setDateRange((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const applyFilter = () => {
    // Update URL search params
    updateSearchParamsAndFetch(filterType, dateRange);

    // Fetch data with new params
    getFinancialSummary();
  };

  const queryFields = {
    buildingId: searchParams.get("buildingId") ?? "",
    from: dateRange.from,
    to: dateRange.to,
    view: appView,
  };

  const getFinancialSummary = async () => {
    try {
      setLoading(true);
      setError(null);
      const { status, data } = await axios.get("/reports/financial-summary", {
        params: queryFields,
      });
      if (status == 200) {
        setApiData(data.data);
      } else {
        toast.error("Error Detected");
        setError("Failed to load data");
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setError(error.response?.data?.message || "An error occurred");
      ErrorHandler(error);
    }
  };

  useEffect(() => {
    getFinancialSummary();
  }, [queryFields.month, queryFields.year, queryFields.buildingId, appView]);

  // Generate a descriptive date range based on filter type
  const getFormattedDateRange = () => {
    switch (filterType) {
      case "thisYear":
        return `Year ${moment().year()}`;
      case "lastYear":
        return `Year ${moment().subtract(1, "year").year()}`;
      case "thisMonth":
        return moment().format("MMMM YYYY");
      case "lastMonth":
        return moment().subtract(1, "month").format("MMMM YYYY");
      case "thisWeek":
        return `Week ${moment().week()}, ${moment().year()}`;
      case "custom":
        return `${moment(dateRange.from).format("MMM DD, YYYY")} - ${moment(
          dateRange.to
        ).format("MMM DD, YYYY")}`;
      default:
        return `${moment(dateRange.from).format("MMM DD, YYYY")} - ${moment(
          dateRange.to
        ).format("MMM DD, YYYY")}`;
    }
  };

  // Get formatted date range display
  const dateRangeDisplay = getFormattedDateRange();

  return (
    <div>
      <Card className="mb-3 no-print filter-controls">
        <CardHeader className="d-flex align-items-center justify-content-between">
          <h4>Financial Summary Report</h4>
        </CardHeader>
        <CardBody>
          <Form>
            <Row className="mb-3">
              <Col xs={12}>
                <h6 className="mb-2">Select Date Range</h6>
                <ButtonGroup>
                  <Button
                    color={
                      filterType === "thisYear" ? "primary" : "outline-primary"
                    }
                    onClick={() => handleFilterTypeChange("thisYear")}
                  >
                    This Year
                  </Button>
                  <Button
                    color={
                      filterType === "lastYear" ? "primary" : "outline-primary"
                    }
                    onClick={() => handleFilterTypeChange("lastYear")}
                  >
                    Last Year
                  </Button>
                  <Button
                    color={
                      filterType === "thisMonth" ? "primary" : "outline-primary"
                    }
                    onClick={() => handleFilterTypeChange("thisMonth")}
                  >
                    This Month
                  </Button>
                  <Button
                    color={
                      filterType === "lastMonth" ? "primary" : "outline-primary"
                    }
                    onClick={() => handleFilterTypeChange("lastMonth")}
                  >
                    Last Month
                  </Button>
                  <Button
                    color={
                      filterType === "thisWeek" ? "primary" : "outline-primary"
                    }
                    onClick={() => handleFilterTypeChange("thisWeek")}
                  >
                    This Week
                  </Button>
                  <Button
                    color={
                      filterType === "custom" ? "primary" : "outline-primary"
                    }
                    onClick={() => handleFilterTypeChange("custom")}
                  >
                    Custom
                  </Button>
                </ButtonGroup>
              </Col>
            </Row>

            {showCustomRange && (
              <Row>
                <Col md={5}>
                  <FormGroup>
                    <Label for="from">From Date</Label>
                    <div className="input-group">
                      <Input
                        type="date"
                        name="from"
                        id="from"
                        value={dateRange.from}
                        onChange={handleDateChange}
                        className="form-control"
                      />
                    </div>
                  </FormGroup>
                </Col>
                <Col md={5}>
                  <FormGroup>
                    <Label for="to">To Date</Label>
                    <div className="input-group">
                      <Input
                        type="date"
                        name="to"
                        id="to"
                        value={dateRange.to}
                        onChange={handleDateChange}
                        className="form-control"
                      />
                    </div>
                  </FormGroup>
                </Col>
                <Col md={2} className="d-flex align-items-end">
                  <Button
                    color="primary"
                    onClick={applyFilter}
                    className="w-100"
                  >
                    Apply Filter
                  </Button>
                </Col>
              </Row>
            )}
          </Form>
        </CardBody>
      </Card>

      <div className="d-flex justify-content-between mb-3 no-print">
        <div></div>
        <div className="d-flex gap-2">
          <Button color="primary" outline onClick={handlePrint}>
            <Printer size={14} className="me-1" />
            Print
          </Button>
          <Button color="primary" outline onClick={handleToPDF}>
            <FilePdf size={14} className="me-1" />
            PDF
          </Button>
        </div>
      </div>

      <div ref={printRef} className="report-container">
        {/* Web version - normal design */}
        <Card className="web-only" style={{ display: "block" }}>
          <CardHeader className="justify-content-between d-flex align-items-center">
            <div className="d-flex align-items-center">
              <img src={Logo} alt="" width={60} />
              <div className="ms-3">
                <h4 className="mb-0">SAKEENA</h4>
                <p className="mb-0">REAL ESTATE</p>
              </div>
            </div>
            <div className="d-flex align-items-center">
              <div>
                <h3 className="mb-0 text-end">ADDOW APARTMENT</h3>
                <p className="text-end mb-0">
                  Report Period: {dateRangeDisplay}
                </p>
              </div>
            </div>
          </CardHeader>

          <CardBody>
            {loading ? (
              <div className="text-center p-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading report data...</p>
              </div>
            ) : error ? (
              <div className="text-center p-5">
                <div className="alert alert-danger">
                  <h4 className="alert-heading">Error!</h4>
                  <p>{error}</p>
                  <hr />
                  <p className="mb-0">
                    Please make sure you have the correct permissions to view
                    this report.
                  </p>
                  <button
                    className="btn btn-outline-danger mt-2"
                    onClick={() => getFinancialSummary()}
                  >
                    Try Again
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* Income Section - Using API Data */}
                <div className="mb-4">
                  <Table responsive bordered className="mb-0">
                    <thead>
                      <tr className="bg-light">
                        <th colSpan={3} className="text-center">
                          INCOME REPORT
                        </th>
                      </tr>
                      <tr>
                        <th>#</th>
                        <th>INCOME TYPE</th>
                        <th className="text-end">AMOUNT ($)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(apiData.income || {}).map(
                        ([key, value], index, array) => (
                          <tr
                            key={key}
                            className={
                              index === array.length - 1 ? "fw-bold" : ""
                            }
                          >
                            <td>{index + 1}</td>
                            <td>{key}</td>
                            <td className="text-end">
                              {formatCurrency(value || 0)}
                            </td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </Table>
                </div>

                {/* Expenses Section - Using API Data */}
                <div className="mb-4">
                  <Table responsive bordered className="mb-0">
                    <thead>
                      <tr className="bg-light">
                        <th colSpan={3} className="text-center">
                          EXPENSES REPORT
                        </th>
                      </tr>
                      <tr>
                        <th>#</th>
                        <th>EXPENSE TYPE</th>
                        <th className="text-end">AMOUNT ($)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(apiData.expenses || {}).map(
                        ([key, value], index, array) => (
                          <tr
                            key={key}
                            className={
                              index === array.length - 1 ? "fw-bold" : ""
                            }
                          >
                            <td>{index + 1}</td>
                            <td>{key}</td>
                            <td className="text-end">
                              {formatCurrency(value || 0)}
                            </td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </Table>
                </div>

                {/* Net Income Section - if available */}
                {apiData.netIncome !== undefined && (
                  <div className="d-flex justify-content-end mb-4">
                    <div
                      style={{
                        minWidth: 340,
                        background: "#fff",
                        // borderRadius: 10, 
                        padding: "2rem 2rem",
                        border: "1px solid #f0f0f0"
                      }}
                    >
                      <table style={{ width: "100%" }}>
                        <tbody>
                          <tr>
                            <td style={{ color: "#888", fontWeight: 500, padding: "8px 0" }}>TOTAL INCOME:</td>
                            <td style={{ textAlign: "right", fontWeight: 600, fontSize: 18 }}>{formatCurrency(apiData.income['Total Income'] || 0)}</td>
                          </tr>
                          <tr>
                            <td style={{ color: "#888", fontWeight: 500, padding: "8px 0" }}>TOTAL EXPENSES:</td>
                            <td style={{ textAlign: "right", fontWeight: 600, fontSize: 18 }}>{formatCurrency(apiData.expenses['Total Expenses'] || 0)}</td>
                          </tr>
                          <tr>
                            <td style={{ color: "#888", fontWeight: 500, padding: "8px 0" }}>{apiData.status}</td>
                            <td style={{
                              textAlign: "right",
                              fontWeight: 700,
                              fontSize: 22,
                              color: apiData.netIncome >= 0 ? "#28a745" : "#dc3545"
                            }}>{formatCurrency(apiData.netIncome || 0)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                <div className="mt-4">
                  <p className="text-muted small">
                    <span className="fw-bold">Note:</span> This financial
                    summary represents all transactions processed during the
                    selected period.
                  </p>
                </div>
              </>
            )}
          </CardBody>
        </Card>

        {/* Print version - MergeReceipt design */}
        <div
          className="print-only d-print-block"
          style={{ display: "none", maxWidth: "210mm", margin: "0 auto" }}
          ref={downloadRef}
        >
          {/* Logo and Header Section - Copied from MergeReceipt */}
          <Row className="mb-4">
            <Col>
              <img
                src={SakeenaLogo}
                alt="Company Logo"
                style={{ maxWidth: "150px" }}
              />
            </Col>
            <Col className="text-center mt-1">
              <img
                src={TextHeader}
                alt="Company Logo"
                style={{ maxWidth: "300px" }}
              />
              <span
                className="badge mt-2 h5"
                style={{
                  backgroundColor: "#f0f0f0",
                  padding: "10px 50px",
                  borderRadius: 20,
                  fontWeight: "bold",
                  color: "black",
                  border: "1px solid #dcdcdc",
                  fontSize: "20px",
                }}
              >
                FINANCIAL SUMMARY
              </span>
            </Col>
            <Col>
              <img
                src={AddowLogo}
                alt="Company Logo"
                style={{ maxWidth: "150px" }}
              />
            </Col>
          </Row>

          {/* Report Header - Similar to MergeReceipt with the blue header */}
          <Row style={{ margin: "10px 5px" }}>
            <Col>
              <div
                className="bg-primary text-white p-1 fw-bold"
                style={{
                  marginRight: "15px",
                }}
              >
                <div className="d-flex justify-content-between fw-bold">
                  <span>REPORT PERIOD: {dateRangeDisplay}</span>
                  <span>DATE ISSUED: {moment().format("DD/MMM/YYYY")}</span>
                </div>
              </div>
            </Col>
          </Row>

          {loading ? (
            <div className="text-center p-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">Loading report data...</p>
            </div>
          ) : error ? (
            <div className="text-center p-5">
              <div className="alert alert-danger">
                <h4 className="alert-heading">Error!</h4>
                <p>{error}</p>
                <hr />
                <p className="mb-0">
                  Please make sure you have the correct permissions to view this
                  report.
                </p>
                <button
                  className="btn btn-outline-danger mt-2"
                  onClick={() => getFinancialSummary()}
                >
                  Try Again
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Income Section - Using API Data */}
              <Row style={{ margin: "10px 5px" }}>
                <Col>
                  <table
                    className="mt-2"
                    style={{
                      width: "100%",
                      marginTop: "10px",
                      borderCollapse: "collapse",
                    }}
                  >
                    <thead>
                      <tr
                        style={{
                          backgroundColor: "#f0f0f0",
                          textAlign: "left",
                          borderBottom: "2px dashed #ccc",
                          height: "40px",
                          padding: "5px",
                        }}
                      >
                        <th colSpan="3" className="text-center">
                          INCOME REPORT
                        </th>
                      </tr>
                      <tr
                        style={{
                          backgroundColor: "#f0f0f0",
                          textAlign: "left",
                          borderBottom: "2px dashed #ccc",
                          height: "40px",
                          padding: "5px",
                        }}
                      >
                        <th style={{ width: "10%" }}>#</th>
                        <th style={{ width: "65%" }}>INCOME TYPE</th>
                        <th style={{ width: "25%", textAlign: "right" }}>
                          AMOUNT ($)
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(apiData.income || {}).map(
                        ([key, value], index, array) => (
                          <tr
                            key={key}
                            className={
                              index === array.length - 1 ? "fw-bold" : ""
                            }
                            style={{
                              borderBottom: "1px solid #ddd",
                              height: "40px",
                              fontSize: "15px",
                              textAlign: "left",
                            }}
                          >
                            <td>{index + 1}</td>
                            <td>{key}</td>
                            <td style={{ textAlign: "right" }}>
                              {formatCurrency(value || 0)}
                            </td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </table>
                </Col>
              </Row>

              {/* Expenses Section - Using API Data */}
              <Row style={{ margin: "10px 5px" }}>
                <Col>
                  <table
                    className="mt-4"
                    style={{
                      width: "100%",
                      marginTop: "10px",
                      borderCollapse: "collapse",
                    }}
                  >
                    <thead>
                      <tr
                        style={{
                          backgroundColor: "#f0f0f0",
                          textAlign: "left",
                          borderBottom: "2px dashed #ccc",
                          height: "40px",
                          padding: "5px",
                        }}
                      >
                        <th colSpan="3" className="text-center">
                          EXPENSES REPORT
                        </th>
                      </tr>
                      <tr
                        style={{
                          backgroundColor: "#f0f0f0",
                          textAlign: "left",
                          borderBottom: "2px dashed #ccc",
                          height: "40px",
                          padding: "5px",
                        }}
                      >
                        <th style={{ width: "10%" }}>#</th>
                        <th style={{ width: "65%" }}>EXPENSE TYPE</th>
                        <th style={{ width: "25%", textAlign: "right" }}>
                          AMOUNT ($)
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(apiData.expenses || {}).map(
                        ([key, value], index, array) => (
                          <tr
                            key={key}
                            className={
                              index === array.length - 1 ? "fw-bold" : ""
                            }
                            style={{
                              borderBottom: "1px solid #ddd",
                              height: "40px",
                              fontSize: "15px",
                              textAlign: "left",
                            }}
                          >
                            <td>{index + 1}</td>
                            <td>{key}</td>
                            <td style={{ textAlign: "right" }}>
                              {formatCurrency(value || 0)}
                            </td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </table>
                </Col>
              </Row>

              {/* Summary Section - Bottom table with totals */}
              <Row className="my-3" style={{ margin: "10px 5px" }}>
                <Col>
                  {apiData.netIncome !== undefined && (
                    <div
                      style={{
                        minWidth: 340,
                        background: "#fff", 
                        padding: "2rem 2rem",
                        border: "1px solid #f0f0f0",
                        margin: "0 auto"
                      }}
                    >
                      <table style={{ width: "100%" }}>
                        <tbody>
                          <tr>
                            <td style={{ color: "#888", fontWeight: 500, padding: "8px 0" }}>TOTAL INCOME:</td>
                            <td style={{ textAlign: "right", fontWeight: 600, fontSize: 18 }}>{formatCurrency(apiData.income['Total Income'] || 0)}</td>
                          </tr>
                          <tr>
                            <td style={{ color: "#888", fontWeight: 500, padding: "8px 0" }}>TOTAL EXPENSES:</td>
                            <td style={{ textAlign: "right", fontWeight: 600, fontSize: 18 }}>{formatCurrency(apiData.expenses['Total Expenses'] || 0)}</td>
                          </tr>
                          <tr>
                            <td style={{ color: "#888", fontWeight: 500, padding: "8px 0" }}>{apiData.status}</td>
                            <td style={{
                              textAlign: "right",
                              fontWeight: 700,
                              fontSize: 22,
                              color: apiData.netIncome >= 0 ? "#28a745" : "#dc3545"
                            }}>{formatCurrency(apiData.netIncome || 0)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}
                </Col>
              </Row>

              {/* Footer Section */}
              <Row className="mb-4">
                <Col className="text-center">
                  <h5>WABILAAHI TOWFIIQ</h5>
                </Col>
              </Row>

              {/* Signature Section */}
              <Row className="mb-2">
                <Col className="text-center">
                  <p>SIGNATURE</p>
                  <p>_______________________________________________</p>
                </Col>
              </Row>

              
            </>
          )}
        </div>
      </div>
    </div>
  );
}
